"use client";

import { useEffect, useMemo, useState } from "react";
import { Table, Tooltip } from "antd";
import { Briefcase, BadgeCheck, Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getOtherProjectsDate } from "../../../redux/otherProjectsSlice/otherProjectsSlice";
import {
    getAllUnits,
    getBusinessSector,
    getAllProjectExecutionPlan,
} from "../../../redux/unitSlice/unitSlice";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../../components/Common/CustomSelect/CustomMultiSelect";
import ActiveProject from "../../../assets/icons/Region.svg";

/* ===================== CONSTANTS ===================== */
const YEAR_TOTAL_SUFFIX = "__YEAR_TOTAL";

const headerStyle = {
    background: "#008dd3",
    color: "#fff",
    fontWeight: 600,
    textAlign: "center",
};

const MONTH_ORDER = [
    "Apr", "May", "Jun",
    "Jul", "Aug", "Sep",
    "Oct", "Nov", "Dec",
    "Jan", "Feb", "Mar",
];

const INDAS_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "Yes", value: "YES" },
    { label: "No", value: "NO" },
];

const PROJECT_TYPE_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "Monitoring", value: "MONITORING" },
    { label: "Other", value: "OTHER" },
];

const SORT_BY_OPTIONS = [
    { label: "IO No.", value: "IO_NO" },
    { label: "Projects", value: "PROJECT" },
    { label: "Difference", value: "DIFFERENCE" },
];

const SORT_ORDER_OPTIONS = [
    { label: "Ascending", value: "ASC" },
    { label: "Descending", value: "DESC" },
];

const formatProjectType = (val) => {
    if (val === "MONITORING") return "Monitoring";
    if (val === "OTHER") return "Other";
    return val || "";
};

// Code-level toggle: when true, project rows whose Balance Revenue (Order
// as on) AND Overall Total are both zero are dropped from the table —
// flip to false to show every project regardless of value.
const HIDE_ZERO_VALUE_PROJECTS = false;

/* ===================== UTILS ===================== */
const getPreviousFYQ4Key = () => {
    const today = new Date();

    const currentFYStart =
        today.getMonth() >= 3
            ? today.getFullYear()
            : today.getFullYear() - 1;

    return `Q4-${currentFYStart - 1}`;
};

const getCurrentFYStartYear = () => {
    const today = new Date();

    return today.getMonth() >= 3
        ? today.getFullYear()
        : today.getFullYear() - 1;
};

const getBalanceTitle = () => {
    const today = new Date();
    const year = today.getFullYear() - 1; // previous year

    const shortYear = String(year).slice(-2);

    return `Order O/s as on 31-12-${shortYear}`;
};

const buildYearTotalColumn = (year) => ({
    title: "Total",
    dataIndex: `${year}${YEAR_TOTAL_SUFFIX}`,
    width: 140,
    align: "center",
    render: (val) => formatNumber(val),
    onHeaderCell: () => ({
        style: {
            background: "#006da4",
            color: "#fff",
            fontWeight: 600,
        },
    }),
});

/* ===================== NORMALIZE ===================== */
export const toNumber = (v) =>
    v ? Number(String(v).replace(/,/g, "")) || 0 : 0;

const getDiffValue = (row) => toNumber(row.BALANCE_REVENUE) - toNumber(row.total);

// Diff (A-B): positive values first (highest to lowest), then negative
// values (highest to lowest, i.e. closest to zero first), then zero last.
const sortByDiffDesc = (rows) => {
    const sign = (v) => (v > 0 ? 0 : v < 0 ? 1 : 2);

    return [...rows].sort((a, b) => {
        const da = getDiffValue(a);
        const db = getDiffValue(b);
        const sa = sign(da);
        const sb = sign(db);

        if (sa !== sb) return sa - sb;
        return db - da;
    });
};

const compareStrings = (a, b) =>
    (a ?? "").toString().localeCompare((b ?? "").toString(), undefined, {
        numeric: true,
        sensitivity: "base",
    });

// "Projects" and "IO No." both sort by an identity field (project name /
// IO number) — since every row for the same project shares that field,
// a plain sort on it naturally keeps all of that project's per-unit rows
// contiguous instead of scattering them by whatever they were sorted on
// before (e.g. Diff, which differs per unit).
const sortRows = (rows, sortBy, sortOrder, unitOrderMap) => {
    if (sortBy === "DIFFERENCE") {
        const sorted = sortByDiffDesc(rows);
        return sortOrder === "ASC" ? sorted.reverse() : sorted;
    }

    const field = sortBy === "IO_NO" ? "IO_NO" : "label";

    return [...rows].sort((a, b) => {
        const cmp = compareStrings(a[field], b[field]);
        if (cmp !== 0) return sortOrder === "ASC" ? cmp : -cmp;

        // Within the same project, order units the same way the Unit
        // filter lists them (allUnitsList), not alphabetically.
        const ua = unitOrderMap?.get(a.MNF_UNIT) ?? Infinity;
        const ub = unitOrderMap?.get(b.MNF_UNIT) ?? Infinity;
        if (ua !== ub) return ua - ub;

        return compareStrings(a.UNIT_NAME, b.UNIT_NAME);
    });
};

const isMonthKey = (key) =>
    /^[A-Z][a-z]{2}-\d{4}$/.test(key) ||
    /^[A-Z][a-z]{2}_\d{2}$/.test(key);

const isQuarterKey = (key) =>
    /^Q[1-4]-\d{4}$/.test(key) ||
    /^Q[1-4]-\d{4}-\d{4}$/.test(key);

const isYearKey = (key) =>
    /^\d{4}-\d{4}$/.test(key);

export const getYearFromKey = (key, viewType) => {
    if (/^\d{4}-\d{4}$/.test(key)) {
        return viewType === "Year" ? key : key.split("-")[0];
    }

    if (/^Q[1-4]-\d{4}-\d{4}$/.test(key)) {
        return `${key.split("-")[1]}-${key.split("-")[2]}`;
    }

    if (/^Q[1-4]-\d{4}$/.test(key)) {
        return key.split("-")[1];
    }

    if (/^[A-Za-z]{3}-\d{4}$/.test(key)) {
        const [month, year] = key.split("-");
        const y = Number(year);
        if (["Jan", "Feb", "Mar"].includes(month)) {
            return `${y - 1}-${y}`;
        }
        return `${y}-${y + 1}`;
    }

    if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
        const shortYear = Number(key.split("_")[1]);
        return `20${shortYear}`;
    }

    return null;
};

const nf2 = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const headerFYLabel = (yearKey) => {
    if (/^\d{4}$/.test(yearKey)) {
        const y = Number(yearKey);
        return `${y}-${y + 1}`;
    }

    if (/^\d{4}-\d{4}$/.test(yearKey)) return yearKey;

    return yearKey;
};

export const formatNumber = (val) => {
    const num = toNumber(val);
    if (val === undefined || val === null || val === "") return "-";
    if (num === 0) return "0";
    return nf2.format(num);
};

const calculateVisibleTotal = (row, viewType) => {
    return Object.entries(row)
        .filter(([key]) => {
            return (
                (viewType === "Year" && /^\d{4}-\d{4}$/.test(key)) ||
                (viewType === "Quarter" &&
                    /^Q[1-4]-\d{4}$/.test(key) &&
                    key !== getPreviousFYQ4Key()) ||
                (viewType === "Month" &&
                    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key))
            );
        })
        .reduce((sum, [, value]) => sum + toNumber(value), 0);
};

const normalizeData = (data, viewType) => {
    const result = {};
    const yearTotals = {};
    if (!data) return result;

    Object.entries(data).forEach(([key, value]) => {
        let normalizedKey = key;

        if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
            const [month, yy] = key.split("_");
            normalizedKey = `${month}-20${yy}`;
        }

        if (
            ["PROJ_NAME", "IO_NO", "REV_NO", "BS_UNIT", "IS_INDAS", "description", "upto", "Total"].includes(key)
        ) return;

        const num = toNumber(value);
        if (num === undefined || num === null) return;

        const year = getYearFromKey(normalizedKey, viewType);

        if (year) {
            const yearStart = /^\d{4}-\d{4}$/.test(year)
                ? Number(year.split("-")[0])
                : Number(year);

            const prevQ4Key = getPreviousFYQ4Key();

            if (yearStart < getCurrentFYStartYear()) {
                if (!(viewType === "Quarter" && normalizedKey === prevQ4Key)) {
                    return;
                }
            }
        }

        if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
        if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
        if (viewType === "Year" && !isYearKey(normalizedKey)) return;

        result[normalizedKey] = (result[normalizedKey] || 0) + num;

        if (viewType === "Quarter") {
            yearTotals[normalizedKey.split("-")[1]] =
                (yearTotals[normalizedKey.split("-")[1]] || 0) + num;
        }
        if (viewType === "Month") {
            const fyYear = getYearFromKey(normalizedKey, viewType);
            yearTotals[fyYear] = (yearTotals[fyYear] || 0) + num;
        }
    });

    Object.entries(yearTotals).forEach(([y, v]) => {
        result[`${y}${YEAR_TOTAL_SUFFIX}`] = v;
    });

    return result;
};

/* ===================== ROW BUILDERS ===================== */
const buildProjectRow = (project, viewType, unit, sector) => {
    const norm = normalizeData(project, viewType);

    const uniqueId = project.IO_NO || project.PROJ_NAME;
    const revision = project.REV_NO || "NO_REV";

    const row = {
        key: `${unit.MNF_UNIT}_${sector.BS_UNIT}_${uniqueId}_${revision}`,
        BS_UNIT: sector.BS_UNIT,
        BS_UNIT_NAME: sector.BS_UNIT_NAME,
        label: project.PROJ_NAME,
        IO_NO: project.IO_NO,
        IS_INDAS: project.IS_INDAS,
        PROJECT_TYPE: project.PROJECT_TYPE,
        MNF_UNIT: unit.MNF_UNIT,
        UNIT_NAME: unit.UNIT_NAME,
        BALANCE_REVENUE:
            project.BALANCE_REVENUE === "-" ? "-" : toNumber(project.BALANCE_REVENUE),
    };

    Object.entries(norm).forEach(([k, v]) => {
        row[k] = v;
    });

    row.total = calculateVisibleTotal(row, viewType);

    return row;
};

const buildGrandTotalRow = (rows) => {
    const combined = {};

    rows.forEach((row) => {
        Object.entries(row).forEach(([key, value]) => {
            if (
                [
                    "key",
                    "label",
                    "BS_UNIT",
                    "BS_UNIT_NAME",
                    "MNF_UNIT",
                    "UNIT_NAME",
                    "IO_NO",
                    "IS_INDAS",
                    "PROJECT_TYPE",
                    "isGrandTotal",
                ].includes(key)
            ) {
                return;
            }

            combined[key] = (combined[key] || 0) + toNumber(value);
        });
    });

    return {
        key: "GRAND_TOTAL",
        BS_UNIT: "Grand Total",
        BS_UNIT_NAME: "Grand Total",
        label: "",
        UNIT_NAME: "",
        IO_NO: "",
        IS_INDAS: null,
        PROJECT_TYPE: null,
        isGrandTotal: true,
        ...Object.fromEntries(
            Object.entries(combined).map(([k, v]) => [k, formatNumber(v)])
        ),
    };
};

/* ===================== COMPONENT ===================== */

const ProjectUnitWisePlanning = () => {
    const dispatch = useDispatch();
    const { allProjectExecutionPlan, allUnitsList, businessSectorListWithoutAll } =
        useSelector((state) => state.unit);
    const { systemDate } = useSelector((state) => state.otherProjects);

    const [viewType, setViewType] = useState("Year");
    const [parameter, setParameter] = useState("REVENUE");
    const [selectedBusinessSector, setSelectedBusinessSector] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState([]);
    const [selectedProject, setSelectedProject] = useState([]);
    const [indasFilter, setIndasFilter] = useState("ALL");
    const [projectTypeFilter, setProjectTypeFilter] = useState("ALL");
    const [sortBy, setSortBy] = useState("DIFFERENCE");
    const [sortOrder, setSortOrder] = useState("DESC");

    // `state.unit.loading` is shared across many unrelated thunks (e.g.
    // getBusinessSector), so it flips back to false as soon as ANY of them
    // resolves — not specifically the execution-plan fetch this table is
    // waiting on. Track loading locally, scoped to just that request.
    const [pageLoading, setPageLoading] = useState(true);

    const fySort = (a, b) => Number(a.split("-")[0]) - Number(b.split("-")[0]);
    const getQuarterLabel = (key) => key.split("-")[0];

    useEffect(() => {
        let isMounted = true;

        setPageLoading(true);

        dispatch(
            getAllProjectExecutionPlan({
                FILTER_PARAMETER: parameter,
                IS_CLEAR: 1,
                IS_BHEL_OVERALL: true,
                notifyWithFilter: true,
            })
        ).finally(() => {
            if (isMounted) {
                setPageLoading(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [dispatch, parameter]);

    useEffect(() => {
        dispatch(getAllUnits());
        dispatch(getBusinessSector());
        dispatch(getOtherProjectsDate("system"));
    }, [dispatch]);

    /* ---------- PROJECT FILTER OPTIONS (FE derived, unit + sector aware) ---------- */
    const projectOptions = useMemo(() => {
        if (!allProjectExecutionPlan?.units) return [];

        const seen = new Map();

        allProjectExecutionPlan.units.forEach((unit) => {
            if (selectedUnit.length && !selectedUnit.includes(unit.MNF_UNIT)) return;

            (unit.other_projects || []).forEach((sector) => {
                if (
                    selectedBusinessSector.length &&
                    !selectedBusinessSector.includes(sector.BS_UNIT)
                ) return;

                (sector.data || []).forEach((project) => {
                    if (!project.IO_NO) return;
                    if (
                        projectTypeFilter !== "ALL" &&
                        project.PROJECT_TYPE !== projectTypeFilter
                    ) return;

                    if (!seen.has(project.IO_NO)) {
                        seen.set(project.IO_NO, project.PROJ_NAME);
                    }
                });
            });
        });

        return Array.from(seen.entries()).map(([ioNo, name]) => ({
            value: ioNo,
            label: (
                <div>
                    <div>{name}</div>
                    <div style={{ fontStyle: "italic", fontSize: 12, color: "#667085" }}>
                        {ioNo}
                    </div>
                </div>
            ),
            searchLabel: `${name} ${ioNo}`,
        }));
    }, [allProjectExecutionPlan, selectedUnit, selectedBusinessSector, projectTypeFilter]);

    // Drop selections that fell out of scope when Unit/Business Sector narrowed the list.
    useEffect(() => {
        setSelectedProject((prev) =>
            prev.filter((p) => projectOptions.some((o) => o.value === p))
        );
    }, [projectOptions]);

    // Diff (A-B) only exists for the Revenue parameter, so sorting by it
    // shouldn't be offered — and shouldn't stay selected — for any other parameter.
    const sortByOptions = useMemo(
        () =>
            parameter === "REVENUE"
                ? SORT_BY_OPTIONS
                : SORT_BY_OPTIONS.filter((o) => o.value !== "DIFFERENCE"),
        [parameter]
    );

    useEffect(() => {
        if (parameter !== "REVENUE" && sortBy === "DIFFERENCE") {
            setSortBy("IO_NO");
        }
    }, [parameter, sortBy]);

    // Same unit ordering the Unit filter dropdown uses, so per-project unit
    // rows sort in that sequence instead of alphabetically.
    const unitOrderMap = useMemo(
        () => new Map((allUnitsList || []).map((u, i) => [u.MNF_UNIT, i])),
        [allUnitsList]
    );

    /* ---------- DATA (flat, per-unit rows — no cross-unit clubbing) ---------- */
    const tableData = useMemo(() => {
        if (!allProjectExecutionPlan?.units) return [];

        let rows = [];

        allProjectExecutionPlan.units.forEach((unit) => {
            (unit.other_projects || []).forEach((sector) => {
                (sector.data || []).forEach((project) => {
                    rows.push(buildProjectRow(project, viewType, unit, sector));
                });
            });
        });

        if (selectedBusinessSector.length) {
            rows = rows.filter((r) => selectedBusinessSector.includes(r.BS_UNIT));
        }

        if (selectedUnit.length) {
            rows = rows.filter((r) => selectedUnit.includes(r.MNF_UNIT));
        }

        if (selectedProject.length) {
            rows = rows.filter((r) => selectedProject.includes(r.IO_NO));
        }

        if (indasFilter !== "ALL") {
            const wantIndas = indasFilter === "YES";
            rows = rows.filter((r) => r.IS_INDAS === wantIndas);
        }

        if (projectTypeFilter !== "ALL") {
            rows = rows.filter((r) => r.PROJECT_TYPE === projectTypeFilter);
        }

        if (HIDE_ZERO_VALUE_PROJECTS) {
            rows = rows.filter((r) => {
                const balance = r.BALANCE_REVENUE === "-" ? 0 : toNumber(r.BALANCE_REVENUE);
                return balance !== 0 || toNumber(r.total) !== 0;
            });
        }

        rows = sortRows(rows, sortBy, sortOrder, unitOrderMap);

        const grandTotal = buildGrandTotalRow(rows);

        return [grandTotal, ...rows];
    }, [
        allProjectExecutionPlan,
        viewType,
        selectedBusinessSector,
        selectedUnit,
        selectedProject,
        indasFilter,
        projectTypeFilter,
        sortBy,
        sortOrder,
        unitOrderMap,
    ]);

    const groupedKeys = useMemo(() => {
        const map = {};
        const prevQ4Key = getPreviousFYQ4Key();
        const currentFYStartYear = getCurrentFYStartYear();

        if (!tableData || !Array.isArray(tableData)) return map;

        tableData.forEach((r) => {
            if (!r || typeof r !== "object") return;
            Object.keys(r).forEach((k) => {
                const valid =
                    (viewType === "Year" && /^\d{4}-\d{4}$/.test(k)) ||
                    (viewType === "Quarter" && /^Q[1-4]-\d{4}$/.test(k)) ||
                    (viewType === "Month" &&
                        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(k));

                if (!valid) return;
                if (k === prevQ4Key) return;

                const year = getYearFromKey(k, viewType);
                if (!year) return;

                const yearStart = /^\d{4}-\d{4}$/.test(year)
                    ? Number(year.split("-")[0])
                    : Number(year);

                if (yearStart < currentFYStartYear) return;

                if (!map[year]) map[year] = new Set();
                map[year].add(k);
            });
        });

        Object.keys(map).forEach((y) => {
            if (!map[y]) return;
            map[y] = Array.from(map[y]).sort((a, b) => {
                if (viewType === "Month") {
                    return MONTH_ORDER.indexOf(a.split("-")[0]) - MONTH_ORDER.indexOf(b.split("-")[0]);
                }
                return a.localeCompare(b);
            });
        });

        return map;
    }, [tableData, viewType]);

    const projectRows = useMemo(
        () => tableData.filter((r) => !r.isGrandTotal),
        [tableData]
    );

    const visibleProjectCount = projectRows.length;

    // Match the "0" the Balance Revenue column actually renders, not the raw
    // pre-rounding number — formatNumber rounds to whole crores, so a value
    // like 0.3 displays as "0" without being exactly zero.
    const zeroBalanceProjectCount = useMemo(
        () =>
            projectRows.filter((r) => formatNumber(r.BALANCE_REVENUE) === "0").length,
        [projectRows]
    );

    const firstQuarter = viewType === "Quarter" ? getPreviousFYQ4Key() : null;

    const columns = useMemo(() => {
        const cols = [
            {
                title: "BS",
                dataIndex: "BS_UNIT",
                width: 100,
                fixed: "left",
                render: (text) => <strong style={{ whiteSpace: "nowrap" }}>{text}</strong>,
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "Project",
                dataIndex: "label",
                width: 340,
                fixed: "left",
                render: (text) => (text ? <strong>{text}</strong> : ""),
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "IO No.",
                dataIndex: "IO_NO",
                width: 140,
                align: "center",
                render: (val) => val || "",
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "Type",
                dataIndex: "PROJECT_TYPE",
                width: 110,
                align: "center",
                render: (val) => formatProjectType(val),
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "INDAS",
                dataIndex: "IS_INDAS",
                width: 90,
                align: "center",
                render: (val) => (val === true ? "Yes" : val === false ? "No" : ""),
                onHeaderCell: () => ({ style: headerStyle }),
            },
            {
                title: "Unit",
                dataIndex: "UNIT_NAME",
                width: 140,
                align: "center",
                render: (val) => val || "",
                onHeaderCell: () => ({ style: headerStyle }),
            },
        ];

        if (parameter === "REVENUE") {
            const dynamicBalanceTitle = systemDate?.STARTDATE
                ? `Order as on ${systemDate.STARTDATE} (A)`
                : getBalanceTitle();

            cols.push({
                title: dynamicBalanceTitle,
                dataIndex: "BALANCE_REVENUE",
                width: 140,
                align: "center",
                render: (val) => {
                    if (val === 0 && val !== undefined) return "0";
                    if (val === 0 || val === undefined || val === null) return "-";
                    return formatNumber(val);
                },
                onHeaderCell: () => ({ style: headerStyle }),
            });
        }

        const groups = Object.keys(groupedKeys).sort(fySort);

        groups.forEach((year) => {
            let keys = groupedKeys[year] || [];

            if (viewType === "Quarter" && firstQuarter) {
                keys = keys.filter((k) => k !== firstQuarter);
            }

            if (viewType === "Year") {
                keys.forEach((k) => {
                    cols.push({
                        title: k,
                        dataIndex: k,
                        align: "center",
                        width: 140,
                        render: (val) => formatNumber(val),
                        onHeaderCell: () => ({ style: headerStyle }),
                    });
                });
                return;
            }

            cols.push({
                title: headerFYLabel(year),
                align: "center",
                onHeaderCell: () => ({ style: headerStyle }),
                children: [
                    ...keys.map((k, i) => ({
                        title: viewType === "Quarter" ? getQuarterLabel(k) : k,
                        dataIndex: k,
                        key: `${year}_${k}_${i}`,
                        align: "center",
                        width: 140,
                        render: (val) => formatNumber(val),
                    })),
                    buildYearTotalColumn(year),
                ],
            });
        });

        cols.push({
            title: parameter === "REVENUE" ? "Overall Total (B)" : "Overall Total",
            dataIndex: "total",
            fixed: "right",
            width: 130,
            align: "center",
            className: "total-column",
            render: (val) => formatNumber(val),
            onHeaderCell: () => ({ style: headerStyle }),
        });

        if (parameter === "REVENUE") {
            cols.push({
                title: "Diff. (A-B)",
                key: "AS_DIFF",
                fixed: "right",
                width: 130,
                align: "center",
                className: "total-column",
                render: (_, record) => formatNumber(getDiffValue(record)),
                onHeaderCell: () => ({ style: headerStyle }),
            });
        }

        return cols;
    }, [groupedKeys, viewType, parameter, firstQuarter, systemDate]);

    /* ===================== RENDER ===================== */

    return (
        <div className="p-5">
            <div className="flex items-center gap-3">
                <div className="flex gap-3 items-center">
                    <img src={ActiveProject} alt="region" />
                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            Report for Projectwise Unitwise Planning
                        </div>
                        <div className="text-gray-500 text-sm">
                            * All figures are in Rupees (in Crores)
                        </div>
                    </div>
                </div>

                <div className="flex gap-3 ml-auto p-2 bg-[#F5F3FF] border border-[#E9D5FF] rounded-2xl">
                    <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-2 shadow-sm">
                        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#EDE9FE] shrink-0">
                            <Briefcase size={18} className="text-[#7C3AED]" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs font-medium text-[#667085] whitespace-nowrap">No. of Records</span>
                            <span className="text-xl font-bold text-[#7C3AED]">{visibleProjectCount}</span>
                        </div>
                    </div>
                    {parameter === "REVENUE" && (
                        <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-2 shadow-sm">
                            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#DCFCE7] shrink-0">
                                <BadgeCheck size={18} className="text-[#16A34A]" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-[#667085] whitespace-nowrap flex items-center gap-1">
                                    No. of Records with Balance Rev. 0
                                    <Tooltip
                                        title="Projects whose Balance Revenue is less than 0.5 round off to 0, so they are counted here as having a Balance Rev of 0."
                                        placement="top"
                                    >
                                        <Info size={13} className="text-[#F59E0B] cursor-pointer shrink-0" />
                                    </Tooltip>
                                </span>
                                <span className="text-xl font-bold text-[#16A34A]">{zeroBalanceProjectCount}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-start gap-4 p-4 mb-4 bg-[#F9FAFB] border border-[#EAECF0] rounded-xl">
                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Business Sector</span>
                        <CustomMultiSelect
                            data={(businessSectorListWithoutAll || []).map((item) => ({
                                label: item.BS_UNIT_DESC,
                                value: item.BS_UNIT,
                            }))}
                            placeholder="Business Sector"
                            value={selectedBusinessSector}
                            onChange={setSelectedBusinessSector}
                            className="w-[220px]"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Unit</span>
                        <CustomMultiSelect
                            data={(allUnitsList || []).map((u) => ({
                                label: u.UNIT_DISPLAY_NAME,
                                value: u.MNF_UNIT,
                            }))}
                            placeholder="Unit"
                            value={selectedUnit}
                            onChange={setSelectedUnit}
                            className="w-[220px]"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Project</span>
                        <CustomMultiSelect
                            data={projectOptions}
                            placeholder="Project"
                            value={selectedProject}
                            onChange={setSelectedProject}
                            className="w-[220px]"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">INDAS</span>
                        <CustomSelectWithFilter
                            data={INDAS_OPTIONS}
                            value={indasFilter}
                            onChange={setIndasFilter}
                            placeholder="INDAS"
                            className="w-[220px]"
                            allowClear={false}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Project Type</span>
                        <CustomSelectWithFilter
                            data={PROJECT_TYPE_OPTIONS}
                            value={projectTypeFilter}
                            onChange={setProjectTypeFilter}
                            placeholder="Project Type"
                            className="w-[220px]"
                            allowClear={false}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">View</span>
                        <CustomSelectWithFilter
                            data={[
                                { label: "Year Wise", value: "Year" },
                                { label: "Quarter Wise", value: "Quarter" },
                                { label: "Month Wise", value: "Month" },
                            ]}
                            value={viewType}
                            onChange={setViewType}
                            placeholder="View"
                            className="w-[220px]"
                            allowClear={false}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Parameter</span>
                        <CustomSelectWithFilter
                            data={[
                                { label: "Revenue", value: "REVENUE" },
                                { label: "Project cost", value: "PROJECT_COST" },
                                { label: "Profit/Loss", value: "PROFIT_LOSS" },
                                { label: "Apportioned cost", value: "APPORTIONED_COST" },
                                { label: "Operational income", value: "OPERATIONAL_INCOME" },
                                { label: "Project profit", value: "PROJECT_PROFIT" },
                                { label: "Net billing", value: "NET_BILLING" },
                                { label: "Cash inflow", value: "CASH_INFLOW" },
                                { label: "Cash outflow", value: "CASH_OUTFLOW" },
                                { label: "Net cash flow", value: "NET_CASH_FLOW" },
                            ]}
                            value={parameter}
                            onChange={setParameter}
                            placeholder="Parameter"
                            className="w-[220px]"
                            allowClear={false}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Sort By</span>
                        <CustomSelectWithFilter
                            data={sortByOptions}
                            value={sortBy}
                            onChange={setSortBy}
                            placeholder="Sort By"
                            className="w-[220px]"
                            allowClear={false}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-[#667085]">Sort Order</span>
                        <CustomSelectWithFilter
                            data={SORT_ORDER_OPTIONS}
                            value={sortOrder}
                            onChange={setSortOrder}
                            placeholder="Sort Order"
                            className="w-[220px]"
                            allowClear={false}
                        />
                    </div>
                </div>

                <Table
                    bordered
                    loading={pageLoading}
                    pagination={false}
                    scroll={{ x: "max-content", y: 600 }}
                    className="execution-plan-table other-projects-table"
                    rowClassName={(row) => (row.isGrandTotal ? "grand-total-row" : "project-row")}
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                />
            </div>
        </div>
    );
};

export default ProjectUnitWisePlanning;
